/** @typedef {typeof DEFAULT_SETTINGS} HologramSettings */

export const DEFAULT_SETTINGS = {
  curtainColorTop: '#cc1a38',
  curtainColorBottom: '#330010',
  curtainOpacity: 0.18,
  showCenterStroke: true,
  showInnerStroke: true,
  showOuterStroke: true,
  strokeSpacing: 0.042,
  strokeOffsetFlip: false,
  centerStrokeColor: '#c4a86a',
  centerStrokeWeight: 0.014,
  centerStrokeOpacity: 0.95,
  innerStrokeColor: '#dcdcdc',
  innerStrokeWeight: 0.009,
  innerStrokeOpacity: 0.88,
  outerStrokeColor: '#dcdcdc',
  outerStrokeWeight: 0.009,
  outerStrokeOpacity: 0.88,
  cornerColor: '#3dffa8',
  cornerRingRadius: 0.22,
  cornerRingOpacity: 0.75,
  cornerCoreOpacity: 1,
  cornerBeamHeight: 16,
  cornerBeamRadius: 0.04,
  cornerBeamOpacity: 0.32,
  showCornerBeamGlow: true,
  cornerBeamGlowRadius: 0.14,
  cornerBeamGlowOpacity: 0.18,
  cornerBeamGlowStrength: 1.15,
  bloomStrength: 0.55,
  pulseSpeed: 2.2,
  scanSpeed: 1.4,
  animationSpeed: 1,
  flagWaveStrength: 0.095,
  pulseAmount: 0.12,
  scanAmount: 0.08,
  cornerPulseSpeed: 2.5,
  cornerPulseAmount: 0.12,
  autoRotate: true,
  autoRotateSpeed: 0.8,
  showMapBackground: true,
  mapOpacity: 0.92,
  mapSize: 16,
  mapOffsetX: 0,
  mapOffsetZ: 0,
  mapRotation: 0,
  trackScale: 0.012,
  circuitFlip: true,
  circuitRotationY: 0,
  showTrackName: true,
  trackNameText: 'DAYTONA',
  trackNameFontSize: 88,
  trackNameColor: '#d4d4d4',
  trackNameOpacity: 0.42,
  trackNamePosX: 0,
  trackNamePosZ: 0,
  trackNameRotation: -32,
  cornerTourDwell: 3,
  cornerTourTravelSpeed: 12,
  cornerTourTravelMin: 0.8,
  cornerTourTravelMax: 20,
  cornerTourTravelMultiplier: 1,
  lapClockwise: true,
  cornerLabelScale: 1,
  trackLift: 8,
  extrudeDepth: 8,
  showElevationCurtain: true,
  useAutoCorners: false,
  curvatureThreshold: 0.28,
};

export const DEFAULT_SVG = '/track.svg';

/** @type {(keyof HologramSettings)[]} */
export const SETTINGS_KEYS = /** @type {const} */ (Object.keys(DEFAULT_SETTINGS));

/**
 * @param {HologramSettings} target
 * @param {Partial<HologramSettings>} [source]
 */
export function applySettingsDefaults(target, source = DEFAULT_SETTINGS) {
  Object.assign(target, source);
}

/**
 * @param {Partial<HologramSettings>} partial
 * @returns {Partial<HologramSettings>}
 */
export function pickSettings(partial) {
  /** @type {Partial<HologramSettings>} */
  const out = {};
  for (const key of SETTINGS_KEYS) {
    if (Object.prototype.hasOwnProperty.call(partial, key)) {
      out[key] = partial[key];
    }
  }
  return out;
}

/**
 * @param {HologramSettings} target
 * @param {Partial<HologramSettings>} partial
 */
export function mergeSettings(target, partial) {
  Object.assign(target, pickSettings(partial));
}

/**
 * @param {keyof HologramSettings} key
 * @param {unknown} value
 */
export function coerceSettingValue(key, value) {
  const def = DEFAULT_SETTINGS[key];
  if (typeof def === 'boolean') return Boolean(value);
  if (typeof def === 'number') {
    const n = Number(value);
    return Number.isFinite(n) ? n : def;
  }
  if (typeof def === 'string') return String(value);
  return def;
}

/**
 * @param {unknown} raw
 * @returns {Partial<HologramSettings>}
 */
export function sanitizeSettings(raw) {
  if (!raw || typeof raw !== 'object') return {};
  /** @type {Partial<HologramSettings>} */
  const out = {};
  for (const key of SETTINGS_KEYS) {
    if (Object.prototype.hasOwnProperty.call(raw, key)) {
      out[key] = coerceSettingValue(key, /** @type {Record<string, unknown>} */ (raw)[key]);
    }
  }
  return out;
}
